import { createTheme } from "@mui/material"

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 576,
      md: 768,
      lg: 992,
      xl: 1200,
      xxl: 1400,

      mobile: 0,
      tablet: 640,
      laptop: 1025,
      desktop: 1200,
    },
  },
  palette: {

    primary: {
      "100": "#dde3ff",
      "200": "#c2cbff",
      light: "#7578ff",
      main: "#635bff",
      dark: "#4e36f5",
    },
    secondary: {
      light: "#f2d952",
      main: "#edc322",
      dark: "#e5b216",
    },
    success: {
      light: "#45c282",
      main: "#22a767",
      dark: "#158652",
    },
    error: {
      light: "#ff697c",
      main: "#f93a52",
      dark: "#e71d36",
    },
    dark: {
      main: "#121621",
    },
    other: {
      color1: "#223645",
      color2: "#7A8A9E",
      color3: "#1585db",
      color4: "#878a99",
      hover1: "#ffffff0a",
    }
  },

  // Customize componets
  components: {
    MuiContainer: {
      styleOverrides: {
        root: {
          ".page-content": {
            padding: "24px",
            borderRadius: "12px",
            border: "1px solid #00000012",
          },
          ".content-listing": {
            width: "100%",
            overflow: "hidden",
            borderRadius: "12px",
            border: "1px solid #00000012",
          },
          ".overview-container": {
            gap: "16px",
            display: "flex",
            flexWrap: "wrap",
            marginBottom: "40px",
            alignItems: "center",
            justifyContent: "space-between",
          },
          ".filters-grid": {
            padding: 24,
            marginBottom: 24,
            borderRadius: 12,
            overflow: "hidden",
            border: "1px solid #00000012",
          },
          ".page-menus": {
            gap: 16,
            padding: 8,
            width: "100%",
            display: "flex",
            overflowX: "auto",
            marginBottom: 24,
            borderRadius: 12,
            alignItems: "center",
            border: "1px solid #00000012",
          },
        }
      }
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          noRecords: "p",
          pageTitle: "h5",
        }
      },
      styleOverrides: {
        root: {
          color: "#223645",
          fontFamily: "Poppins",
          variants: [
            {
              props: { variant: "pageTitle" },
              style: {
                fontSize: "32px",
                fontWeight: "500",
                lineHeight: "1.2",
                whiteSpace: "nowrap",
              }
            },
            {
              props: { variant: "noRecords" },
              style: {
                color: "#110e24",
                fontSize: "24px",
                minWidth: "100px",
                fontWeight: "500",
                textWrap: "nowrap",
                overflow: "hidden",
                textAlign: "center",
                fontFamily: "Poppins",
                textOverflow: "ellipsis",
                textTransform: "capitalize",
              }
            },
          ]
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          whiteSpace: "nowrap",
          fontFamily: "Poppins",
          textTransform: "initial",
          transition: "all 400ms ease",
          ":hover": { boxShadow: "none" },
          variants: [
            {
              props: { variant: "sidebarMenu" },
              style: {
                width: "100%",
                fontWeight: "400",
                justifyContent: "start",
                textTransform: "capitalize",
                ":hover": { backgroundColor: "#ffffff0a" },
                ".MuiButton-endIcon": { marginLeft: "auto" },
                ".MuiButton-startIcon svg": { fontSize: "18px" }
              }
            },
            {
              props: { variant: "activeSidebarMenu" },
              style: {
                width: "100%",
                color: "#fff",
                fontWeight: "400",
                justifyContent: "start",
                backgroundColor: "#635bff",
                textTransform: "capitalize",
                ":hover": { backgroundColor: "#7578ff" },
                ".MuiButton-endIcon": { marginLeft: "auto" },
                ".MuiButton-startIcon svg": { fontSize: "18px" }
              }
            },

            {
              props: { variant: "mobileMenu" },
              style: {
                width: "100%",
                justifyContent: "flex-start",
                textTransform: "capitalize",
                fontWeight: 500,
                color: "#0f0f0fff",
                borderRadius: "6px",
                px: 1.5,
                py: 1,
                "&:hover": { backgroundColor: "#f4f4ff" },
              },
            },
            {
              props: { variant: "activeMobileMenu" },
              style: {
                width: "100%",
                justifyContent: "flex-start",
                textTransform: "capitalize",
                fontWeight: 500,
                color: "#fff",
                backgroundColor: "#635bff",
                borderRadius: "6px",
                px: 1.5,
                py: 1,
                "&:hover": { backgroundColor: "#7578ff" },
              }
            }
          ]
        }
      }
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          width: "100%",
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontFamily: "Poppins",
          "input::placeholder": {
            color: "#00000099"
          },
          variants: [
            {
              props: { variant: "filled" },
              style: {
                ":after": { display: "none" },
                ":before": { display: "none" },
              }
            }
          ]
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontWeight: "500",
          marginBottom: "4px",
          fontFamily: "Poppins",
        }
      }
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          variants: [
            {
              props: { variant: "faqListing" },
              style: {
                width: "100%",
                boxShadow: "none",
                border: "1px solid",
                borderColor: "#0000001f",
                borderRadius: "6px !important",
                ".MuiButtonBase-root": {
                  color: "#223645",
                  fontSize: "15px",
                },
                "&.Mui-expanded": { margin: "0" },
                "&.Mui-expanded .MuiButtonBase-root": {
                  color: "#635bff",
                  backgroundColor: "#dde3ff",
                }
              }
            }
          ]
        }
      }
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          variants: [
            {
              props: { className: "adminTable1" },
              style: {
                ".MuiTableCell-root": {
                  whiteSpace: "nowrap",
                  fontFamily: "Poppins",
                },
                "th": { backgroundColor: "#dde3ff" },
                "tr:hover": { backgroundColor: "#00000012" },
                "td .MuiIconButton-root": {
                  color: "#7578ff",
                  transition: "all 400ms ease",
                  ":hover": {
                    color: "#635bff",
                    backgroundColor: "#dde3ff",
                  },
                },
                "td .MuiChip-label": {
                  color: "white",
                  lineHeight: "1",
                  overflow: "initial",
                },
              }
            },
            {
              props: { className: "adminTable2" },
              style: {
                ".MuiTableCell-root": {
                  whiteSpace: "nowrap",
                  fontFamily: "Poppins",
                },
                "th": { fontWeight: "500" },
                "table": { tableLayout: "fixed" },
                "tr:hover": { backgroundColor: "#00000012" },
                "td .MuiIconButton-root": {
                  color: "#7578ff",
                  transition: "all 400ms ease",
                  ":hover": {
                    color: "#635bff",
                    backgroundColor: "#dde3ff",
                  },
                },
              }
            },
          ]
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          variants: [
            {
              props: { variant: "summaryTile" },
              style: {
                padding: "16px 24px",
                overflow: "hidden",
                borderRadius: "8px",
                position: "relative",
                backgroundColor: "#efecec",
                ":after": {
                  inset: "0",
                  width: "6px",
                  content: "''",
                  right: "initial",
                  position: "absolute",
                  backgroundColor: "#635bff"
                }
              }
            },
            {
              props: { paperVariant: "filePostViewA" },
              style: {
                padding: 4,
                maxWidth: 80,
                fontSize: 60,
                width: "100%",
                display: "grid",
                borderRadius: 6,
                fontWeight: "500",
                overflow: "hidden",
                aspectRatio: "1/1",
                position: "relative",
                placeContent: "center",
                backgroundColor: "#eee",
                "span": { fontSize: 12, textAlign: "center" },
                "div": {
                  inset: "0",
                  opacity: "0",
                  fontSize: 32,
                  color: "white",
                  display: "grid",
                  cursor: "pointer",
                  position: "absolute",
                  placeContent: "center",
                  transition: "all ease 200ms",
                  backgroundColor: "#3c3c3c5e",
                  ":hover": { opacity: "1" },
                },
              }
            },
          ]
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          variants: [
            {
              props: { variant: "AutoComp" },
              style: {
                ".MuiChip-root": {
                  fontSize: "14px",
                  color: "#223645",
                  borderRadius: "4px",
                  fontFamily: "Poppins",
                  backgroundColor: "#c2cbff",
                  ".MuiSvgIcon-root": {
                    color: "#ef9a9a",
                    transition: "all 400ms ease",
                    ":hover": { color: "#ef5350" }
                  }
                },
              }
            }
          ]
        }
      }
    },
  }
});

export default theme